export type AuthenticatedUser = {
  id: string;
  email: string;
  timezone: string;
  displayName: string;
  avatarMediaId: string | null;
  roles: Array<"super_admin" | "administrator" | "student">;
};

type ApiErrorResponse = { error?: { code?: string; message?: string } };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(body.error?.message ?? "Something went wrong. Please try again.");
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function getCurrentUser(): Promise<AuthenticatedUser> {
  const response = await request<{ user: AuthenticatedUser }>("/api/v1/me");
  return response.user;
}

export async function signIn(email: string, password: string): Promise<void> {
  await request("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function signOut(): Promise<void> {
  await request("/api/v1/auth/logout", { method: "POST" });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await request("/api/v1/auth/password-reset/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export type PublicationStatus = "draft" | "scheduled" | "published" | "archived";
export type MediaStatus = "uploading" | "validating" | "ready" | "reviewed" | "failed" | "archived";

export type AdminUser = {
  id: string;
  email: string;
  displayName: string;
  status: "active" | "deactivated";
  timezone: string;
  lastLoginAt: string | null;
  createdAt: string;
  roles: Array<"super_admin" | "administrator" | "student">;
};

export type AdminSection = {
  id: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  iconCode: string;
  status: PublicationStatus;
  sortKey: string;
  publishedAt: string | null;
  scheduledAt: string | null;
};

export type AdminCourse = {
  id: string;
  sectionId: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  status: PublicationStatus;
  certificateEnabled: boolean;
  sortKey: string;
  publishedAt: string | null;
  scheduledAt: string | null;
};

export type AdminLesson = {
  id: string;
  courseId: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  status: PublicationStatus;
  sortKey: string;
  publishedAt: string | null;
  scheduledAt: string | null;
};

export type AdminLearningTreeVideo = { id: string; lessonId: string; mediaAssetId: string; slug: string; title: string; durationSeconds: number | null; sortKey: string };
export type AdministratorDraftPreviewVideo = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  durationSeconds: number | null;
  menu: { id: string; slug: string; title: string };
  course: { id: string; slug: string; title: string };
  chapter: { id: string; slug: string; title: string };
};
export type AdminLearningTreeMenu = AdminSection & { courses: Array<AdminCourse & { chapters: Array<AdminLesson & { videos: AdminLearningTreeVideo[] }> }> };

export type MediaAsset = {
  id: string;
  kind: "video" | "lesson_material" | "avatar" | "discussion_image";
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  status: MediaStatus;
  durationSeconds: number | null;
  createdAt: string;
  reviewedAt: string | null;
  attachedToChapter: boolean;
};

export type AdminMediaCatalogVideo = {
  mediaAssetId: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  status: MediaStatus;
  durationSeconds: number | null;
  createdAt: string;
  lessonVideoId: string | null;
  lessonVideoTitle: string | null;
  lessonVideoSlug: string | null;
  chapterId: string | null;
  chapterTitle: string | null;
  chapterSlug: string | null;
  courseId: string | null;
  courseTitle: string | null;
  courseSlug: string | null;
  menuId: string | null;
  menuTitle: string | null;
  menuSlug: string | null;
};

export type LearningVideo = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  durationSeconds: number | null;
  progress: {
    watchedCoverageBasisPoints: number;
    resumeSeconds: number;
    completedAt: string | null;
    firstViewedAt: string | null;
    lastActivityAt: string | null;
  };
};

export type LearningChapter = {
  id: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  videos: LearningVideo[];
};

export type LearningCourseDetail = {
  id: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  certificateEnabled: boolean;
  menu: { id: string; slug: string; title: string };
  section: { id: string; slug: string; title: string };
  chapters: LearningChapter[];
  lessons: LearningChapter[];
};

export type LearningSection = {
  id: string;
  slug: string;
  title: string;
  descriptionRichtext: string | null;
  iconCode: string;
  courses: Array<{
    id: string;
    slug: string;
    title: string;
    descriptionRichtext: string | null;
    certificateEnabled: boolean;
    progress: {
      progressBasisPoints: number;
      completedLessonCount: number;
      totalLessonCount: number;
      completedAt: string | null;
      updatedAt: string | null;
    };
  }>;
};

export type VideoUploadSession = {
  mediaAssetId: string;
  uploadSessionId: string;
  filename: string;
  uploadUrl: string;
  expiresInSeconds: number;
};

export async function listAdminUsers(): Promise<AdminUser[]> {
  return (await request<{ users: AdminUser[] }>("/api/v1/admin/users")).users;
}

export async function createAdminUser(input: { email: string; displayName: string; password: string; role: "administrator" | "student"; timezone: string }): Promise<void> {
  await request("/api/v1/admin/users", { method: "POST", body: JSON.stringify(input) });
}

export async function setUserActive(userId: string, active: boolean): Promise<void> {
  await request(`/api/v1/admin/users/${userId}/${active ? "reactivate" : "deactivate"}`, { method: "POST" });
}

export async function listAdminSections(): Promise<AdminSection[]> {
  return (await request<{ sections: AdminSection[] }>("/api/v1/admin/sections")).sections;
}

export async function getAdminLearningTree(): Promise<AdminLearningTreeMenu[]> {
  return (await request<{ menus: AdminLearningTreeMenu[] }>("/api/v1/admin/learning-tree")).menus;
}

export async function createSection(input: { title: string; descriptionRichtext?: string; iconCode?: string; status?: PublicationStatus }): Promise<void> {
  await request("/api/v1/admin/sections", { method: "POST", body: JSON.stringify(input) });
}

export async function publishSection(sectionId: string): Promise<void> {
  await request(`/api/v1/admin/sections/${sectionId}`, { method: "PATCH", body: JSON.stringify({ status: "published" }) });
}

export async function listCourses(sectionId: string): Promise<AdminCourse[]> {
  return (await request<{ courses: AdminCourse[] }>(`/api/v1/admin/sections/${sectionId}/courses`)).courses;
}

export async function createCourse(sectionId: string, input: { title: string; descriptionRichtext?: string; certificateEnabled?: boolean; status?: PublicationStatus }): Promise<void> {
  await request(`/api/v1/admin/sections/${sectionId}/courses`, { method: "POST", body: JSON.stringify(input) });
}

export async function publishCourse(courseId: string): Promise<void> {
  await request(`/api/v1/admin/courses/${courseId}`, { method: "PATCH", body: JSON.stringify({ status: "published" }) });
}

export async function listLessons(courseId: string): Promise<AdminLesson[]> {
  return (await request<{ lessons: AdminLesson[] }>(`/api/v1/admin/courses/${courseId}/lessons`)).lessons;
}

export async function createLesson(courseId: string, input: { title: string; descriptionRichtext?: string; status?: PublicationStatus }): Promise<void> {
  await request(`/api/v1/admin/courses/${courseId}/lessons`, { method: "POST", body: JSON.stringify(input) });
}

export async function publishLesson(lessonId: string): Promise<void> {
  await request(`/api/v1/admin/lessons/${lessonId}`, { method: "PATCH", body: JSON.stringify({ status: "published" }) });
}

export async function listMediaAssets(): Promise<MediaAsset[]> {
  return (await request<{ assets: MediaAsset[] }>("/api/v1/admin/media-assets")).assets;
}

export async function createVideoUploadSessions(files: File[]): Promise<VideoUploadSession[]> {
  const response = await request<{ uploads: VideoUploadSession[] }>("/api/v1/admin/media/videos/upload-sessions", {
    method: "POST",
    body: JSON.stringify({ files: files.map((file) => ({ filename: file.name, contentType: file.type, sizeBytes: file.size })) }),
  });
  return response.uploads;
}

export async function uploadToPresignedUrl(url: string, file: File, onProgress: (loaded: number, total: number) => void): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("content-type", "video/mp4");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    xhr.onerror = () => reject(new Error("The video upload could not be completed."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error("The video storage service rejected the upload."));
    };
    xhr.send(file);
  });
}

export async function completeVideoUpload(uploadSessionId: string): Promise<{ id: string; status: MediaStatus }> {
  const response = await request<{ mediaAsset: { id: string; status: MediaStatus } }>(`/api/v1/admin/media/upload-sessions/${uploadSessionId}/complete`, { method: "POST", body: JSON.stringify({}) });
  return response.mediaAsset;
}

export async function reviewVideoAsset(mediaAssetId: string, durationSeconds: number): Promise<void> {
  await request(`/api/v1/admin/media-assets/${mediaAssetId}/review`, { method: "POST", body: JSON.stringify({ durationSeconds }) });
}

export async function deleteMediaAsset(mediaAssetId: string): Promise<void> {
  await request(`/api/v1/admin/media-assets/${mediaAssetId}`, { method: "DELETE" });
}

export async function listAdminMediaCatalog(): Promise<AdminMediaCatalogVideo[]> {
  return (await request<{ videos: AdminMediaCatalogVideo[] }>("/api/v1/admin/media-catalog")).videos;
}

export async function getAdminMediaPreview(mediaAssetId: string): Promise<{ playbackUrl: string; expiresInSeconds: number }> {
  return request(`/api/v1/admin/media-assets/${mediaAssetId}/preview`, { method: "POST", body: JSON.stringify({}) });
}

export async function moveLessonVideo(lessonVideoId: string, chapterId: string): Promise<void> {
  await request(`/api/v1/admin/lesson-videos/${lessonVideoId}/move`, { method: "PATCH", body: JSON.stringify({ chapterId }) });
}

export async function reorderLessonVideos(chapterId: string, videoIds: string[]): Promise<void> {
  await request(`/api/v1/admin/lessons/${chapterId}/video-order`, { method: "PATCH", body: JSON.stringify({ videoIds }) });
}

export async function attachVideoToLesson(chapterId: string, input: { mediaAssetId: string; title?: string; description?: string }): Promise<void> {
  await request(`/api/v1/admin/lessons/${chapterId}/videos`, { method: "POST", body: JSON.stringify(input) });
}

export async function getAdministratorDraftPreview(lessonVideoId: string): Promise<AdministratorDraftPreviewVideo> {
  return (await request<{ video: AdministratorDraftPreviewVideo }>(`/api/v1/admin/draft-preview/videos/${lessonVideoId}`)).video;
}

export async function getAdministratorDraftPlayback(lessonVideoId: string): Promise<{ playbackUrl: string; expiresInSeconds: number; video: { id: string; durationSeconds: number | null } }> {
  return request(`/api/v1/admin/draft-preview/videos/${lessonVideoId}/playback`, { method: "POST", body: JSON.stringify({}) });
}

export async function getLearningSections(): Promise<LearningSection[]> {
  return (await request<{ sections: LearningSection[] }>("/api/v1/learning/sections")).sections;
}

export async function getLearningCourse(courseId: string): Promise<LearningCourseDetail> {
  return (await request<{ course: LearningCourseDetail }>(`/api/v1/learning/courses/${courseId}`)).course;
}

export async function getVideoPlayback(videoId: string): Promise<{ playbackUrl: string; expiresInSeconds: number; video: { id: string; durationSeconds: number | null } }> {
  return request(`/api/v1/learning/videos/${videoId}/playback`, { method: "POST", body: JSON.stringify({}) });
}

export async function sendVideoHeartbeat(input: { lessonVideoId: string; positionSeconds: number; segmentStartSeconds: number; segmentEndSeconds: number }): Promise<{ watchedCoverageBasisPoints: number; completed: boolean; resumeSeconds: number }> {
  const response = await request<{ progress: { watchedCoverageBasisPoints: number; completed: boolean; resumeSeconds: number } }>(`/api/v1/learning/videos/${input.lessonVideoId}/heartbeat`, { method: "POST", body: JSON.stringify(input) });
  return response.progress;
}
